import { useEchartsContainerWidth } from '@app/src/hooks/useEchartsContainerWidth'
import { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import type { CartesianAxisOption } from '@app/src/types/echarts'
import { getMinMaxOnYAxisCountCharts } from '@app/src/utils/statisticsUtils'
import type { SpendClassification } from '@app/src/wf-constants'
import { spendClassificationOrder } from '@app/src/wf-constants'
import { useTheme } from '@mui/material'
import type { EChartsOption } from 'echarts'
import type ReactEChartsCore from 'echarts-for-react/lib/core'
import type { XAXisOption, YAXisOption } from 'echarts/types/dist/shared'
import type React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { OptionResultItemMultiPeriod } from '../../../../DataHubScene'
import { useSpendConfigsUtils } from './useSpendConfigsUtils'

interface OptionMultiPeriodChartConfigsParams {
  optionItemResult: { groupName: string; options: OptionResultItemMultiPeriod[] }[]
  selectedChartType: ChartType
  itemsInOther: string[]
  echartsRef: React.MutableRefObject<ReactEChartsCore | null>
}

const APPROXIMATE_WIDTH_OF_YAXIS = 60

const getAxisLabelWidth = (widthOfChart: number | undefined, existingOptions: string[]) => {
  if (!widthOfChart) return undefined
  return (widthOfChart - APPROXIMATE_WIDTH_OF_YAXIS) / existingOptions.length
}

const getMinMaxYAxis = (optionItemResult: { options: OptionResultItemMultiPeriod[] }[]) => {
  const optionValues = optionItemResult.flatMap(op => op.options.map(op => op.count))
  return getMinMaxOnYAxisCountCharts(optionValues)
}

export const useOptionSpendChartConfigs = ({
  optionItemResult,
  selectedChartType,
  itemsInOther,
  echartsRef,
}: OptionMultiPeriodChartConfigsParams) => {
  const { formatMessage } = useIntl()
  const { typography } = useTheme()
  const widthOfChart = useEchartsContainerWidth(echartsRef)
  const { getSpendSeriesColor, formatGroupName } = useSpendConfigsUtils()
  const isHorizontalBarChart = selectedChartType === ChartType.Horizontal

  const orderedOptionItemResult = useMemo(
    () =>
      optionItemResult.sort(
        (a, b) => spendClassificationOrder.indexOf(b.groupName) - spendClassificationOrder.indexOf(a.groupName),
      ),
    [optionItemResult],
  )

  const itemsForChartType = useMemo(
    () =>
      selectedChartType === ChartType.Horizontal
        ? orderedOptionItemResult.map(r => ({ ...r, options: r.options.toReversed() }))
        : orderedOptionItemResult,
    [selectedChartType, orderedOptionItemResult],
  )

  const existingOptions = itemsForChartType?.[0]?.options.map(op => op.name)

  const { min, max } = useMemo(() => getMinMaxYAxis(optionItemResult), [optionItemResult])

  const categoryAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'category',
      data: existingOptions,
      triggerEvent: true,
      axisLabel: {
        interval: 0,
        width: getAxisLabelWidth(widthOfChart, existingOptions),
        overflow: 'break',
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [existingOptions, typography.body2.fontSize, typography.fontFamily],
  )

  const valueAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'value',
      name: formatMessage({ id: 'statistics.dataInsights.count' }),
      nameLocation: 'middle',
      nameGap: 35,
      min: min,
      max: max,
      minInterval: 1,
      nameTextStyle: {
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [formatMessage, typography.body2.fontSize, typography.fontFamily, min, max],
  )

  const horizontalBarChartOptions: EChartsOption = useMemo(() => {
    const dataSeries: EChartsOption['series'] = itemsForChartType.map((f, seriesIndex) => ({
      name: formatGroupName(f.groupName),
      color: getSpendSeriesColor(f.groupName as SpendClassification),
      type: 'bar',
      stack: 'chart',
      barCategoryGap: '60%',
      data: f.options.map(op => ({
        value: op.count,
        name: op.name,
        label:
          seriesIndex === 0
            ? {
                show: true,
                position: [0, -14],
                align: 'left',
                formatter: '{b}',
                fontFamily: typography.fontFamily,
                fontSize: typography.body2.fontSize,
                overflow: 'truncate',
                width: widthOfChart ? widthOfChart : undefined,
              }
            : undefined,
        groupName: f.groupName,
        answersSelected: op.isOther ? itemsInOther : [op.name],
      })),
      label: {
        show: false,
      },
    }))

    const xAxis = valueAxis as XAXisOption
    const yAxis = { ...categoryAxis, show: false } as YAXisOption

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: false,
        left: 15,
        right: 7,
        top: 30,
        bottom: 90,
      },
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 10,
        left: 0,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      series: dataSeries,
    }
  }, [categoryAxis, itemsForChartType, typography.body2.fontSize, typography.fontFamily, valueAxis])

  const verticalBarChartOptions: EChartsOption = useMemo(() => {
    const dataSeries: EChartsOption['series'] = itemsForChartType.map((f, seriesIndex) => ({
      name: formatGroupName(f?.groupName),
      color: getSpendSeriesColor(f.groupName as SpendClassification),
      type: 'bar',
      barCategoryGap: '35%',
      data: f?.options.map(op => ({
        value: op.count,
        name: op.name,
        groupName: f.groupName,
        answersSelected: op.isOther ? itemsInOther : [op.name],
        label: {
          position: 'top',
        },
      })),
    }))

    const xAxis = categoryAxis as XAXisOption
    const yAxis = valueAxis as YAXisOption

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: true,
        left: 35,
        right: 7,
        top: 30,
        bottom: 50,
      },
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 10,
        left: 0,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      series: dataSeries,
    }
  }, [categoryAxis, itemsForChartType, typography.body2.fontSize, typography.fontFamily, valueAxis])

  return isHorizontalBarChart ? horizontalBarChartOptions : verticalBarChartOptions
}
